import { useEffect, useState } from "react";
import { ShieldCheck, ExternalLink, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Loader, PageHeader, RichText } from "@/components/common";

function LessonGuidance({ l, open, onToggle }) {
  return (
    <div id={l.lesson_id} className="border-t border-[#EADEC9] first:border-0" data-testid={`guidance-lesson-${l.lesson_id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 py-3.5 text-left" data-testid={`guidance-toggle-${l.lesson_id}`}>
        <span className="text-[11px] font-mono text-[#8C9BA5] w-16 shrink-0">{l.lesson_id}</span>
        <span className="flex-1 text-sm font-semibold">{l.title}</span>
        <ChevronDown className={`h-4 w-4 text-[#8C9BA5] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 pl-0 sm:pl-[76px] space-y-3 fade-up">
          {l.guidance && <RichText text={l.guidance.split(/\nOfficial references:/)[0]} className="text-sm text-[#5C6B73]" />}
          <ul className="space-y-1.5">
            {l.sources.map((s) => (
              <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-[#179B80] font-semibold inline-flex items-start gap-1.5"><ExternalLink className="h-3.5 w-3.5 mt-0.5 shrink-0" />{s.name}</a></li>
            ))}
          </ul>
          {l.last_verified && <div className="text-[11px] text-[#8C9BA5]">Last verified: {l.last_verified}</div>}
        </div>
      )}
    </div>
  );
}

export default function OfficialSources() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(window.location.hash.slice(1));
  useEffect(() => {
    api.get("/official-sources").then((r) => {
      setD(r.data);
      const h = window.location.hash.slice(1);
      if (h) setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    });
  }, []);
  if (!d) return <Loader />;
  return (
    <div data-testid="official-sources-page">
      <PageHeader eyebrow="Official guidance" title="Based on official Mauritius sources" subtitle="The official documents behind each Sidebizz lesson. For legal, tax, licensing or regulatory decisions, confirm current requirements with the relevant authority or a qualified professional." />
      <section className="sb-card p-5 mb-5" data-testid="official-sources-list">
        <div className="sb-eyebrow mb-3">Official sources</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {d.sources.map((s) => (
            <a key={s.key} href={s.url} target="_blank" rel="noreferrer" className="rounded-xl border border-[#EADEC9] p-4 hover:border-[#1DB99A] transition-colors" data-testid={`official-source-${s.key}`}>
              <div className="flex items-center gap-2 text-[11px] font-bold text-[#179B80]"><ShieldCheck className="h-3.5 w-3.5" />{s.authority}</div>
              <div className="font-semibold text-sm mt-1">{s.name}</div>
              <div className="text-xs text-[#5C6B73] mt-1 line-clamp-2">{s.supports}</div>
              {s.last_verified && <div className="text-[10px] text-[#8C9BA5] mt-2">Last checked: {s.last_verified}</div>}
            </a>
          ))}
        </div>
      </section>
      <div className="space-y-4">
        {d.stages.map((st) => (
          <section key={st.key} className="sb-card px-5 py-2" data-testid={`guidance-stage-${st.key.replace(" ", "-")}`}>
            <div className="sb-eyebrow pt-3 pb-1">{st.name}</div>
            {st.lessons.map((l) => <LessonGuidance key={l.lesson_id} l={l} open={open === l.lesson_id} onToggle={() => setOpen(open === l.lesson_id ? "" : l.lesson_id)} />)}
          </section>
        ))}
      </div>
    </div>
  );
}
